/**
 * Deterministic waveform bar heights.
 *
 * The prototype called `randomHeights(n)` — which uses Math.random() —
 * directly inside JSX during render:
 *
 *     <Waveform heights={randomHeights(26)} playing={...} />
 *
 * Because that runs on every render, every waveform in the app
 * re-randomized its bars on every state change anywhere — including on
 * each keystroke in an unrelated textarea. The waveforms visibly jittered
 * and never held still.
 *
 * The fix is to derive heights from a stable seed (the song or version id)
 * instead of from Math.random(). Same seed always yields the same shape, so
 * the bars are stable across renders and remounts with no memoization
 * required. The visual character of the output is unchanged: the same
 * 6–26px range as the original.
 */

/** FNV-1a style string hash. Deterministic, fast, no dependencies. */
function hashSeed(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * mulberry32 — a small, fast, well-distributed seeded PRNG.
 * Returns a function producing floats in [0, 1).
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Builds `n` bar heights deterministically from `seed`.
 *
 * Matches the original range of 6 + round(rand * 20) => 6..26 px.
 *
 * @param seed - Stable identifier, e.g. a song id.
 * @param n - Number of bars.
 */
export function seededHeights(seed: string | number, n: number): number[] {
  const rand = mulberry32(hashSeed(String(seed ?? 'Lyrixa')));
  return Array.from({ length: n }, () => 6 + Math.round(rand() * 20));
}

/** The range `seededHeights` produces, named so the rescale below cannot drift from it. */
const SEEDED_MIN = 6;
const SEEDED_MAX = 26;

/**
 * How many bars the small playing indicator draws.
 *
 * Four. It has to fit inside a 32px circle beside nothing else — four 3px bars with 2px
 * between them is 18px, which leaves the circle its padding — and it has to read as
 * movement rather than as a chart, which is what more bars at this size would look like.
 */
export const INDICATOR_BARS = 4;

/** The indicator's shortest and tallest bar, in px. Sized against a 13px glyph. */
export const INDICATOR_MIN_PX = 5;
export const INDICATOR_MAX_PX = 14;

/**
 * Bar heights for the small "this one is playing" indicator, from the same seed.
 *
 * A RESCALE OF seededHeights, NOT A SECOND GENERATOR, and that is the point: a song's
 * shape is a fact about the song, so the four bars on its library row are the first four
 * of the same waveform the transport draws for it. A separate PRNG here would mean one
 * song had two shapes depending on which surface you looked at.
 *
 * Rescaled rather than passed a range, because `seededHeights` is called with five
 * different bar counts across the app and its 6–26px range is what every one of those
 * surfaces is drawn against. Mapping the output is what leaves them untouched.
 *
 * Clamped to whole pixels: a 5.5px bar is a blurry bar, and the values go straight into
 * an inline `height`.
 */
export function indicatorHeights(seed: string | number): number[] {
  const span = SEEDED_MAX - SEEDED_MIN;
  const range = INDICATOR_MAX_PX - INDICATOR_MIN_PX;
  return seededHeights(seed, INDICATOR_BARS).map((h) =>
    Math.round(INDICATOR_MIN_PX + ((h - SEEDED_MIN) / span) * range)
  );
}
